import React from "react";
import PanelErrorBoundary from "./PanelErrorBoundary";
import CardWatermark from "./CardWatermark";
import { SkeletonCard } from "./Skeleton";

// Per-panel metadata, keyed by the panelKey base (modality prefix stripped). The
// backend say[panelKey] sentence and an explicit `description` prop both still win;
// `desc` is the last-resort fallback so NO card is ever title-only (user ruling
// 2026-09-04), and `accent` is a subtle top-rule colour that gives each card a
// faint identity. The descriptions are plain descriptive subtitles — never a metric.
const PANEL_META = {
  funnel:        { desc: "Each stage of the conversation, and where people were lost.", accent: "sky" },
  barriers:      { desc: "Why conversations didn't convert, grouped by who can fix it.", accent: "amber" },
  greeting_loss: { desc: "How long callers waited through the greeting, and how many never spoke.", accent: "rose" },
  website_gaps:  { desc: "Questions guests asked in their own words.", accent: "violet" },
  demand:        { desc: "What people wanted, by stay type.", accent: "teal" },
  followups:     { desc: "People worth reaching back out to.", accent: "emerald" },
  in_progress:   { desc: "Reservations the agent built that aren't paid for yet.", accent: "amber" },
  time_saved:    { desc: "Staff time the agent saved you, valued at your hourly rate.", accent: "emerald" },
  workload:      { desc: "How many people the agent handled, and how many without a person.", accent: "sky" },
  attribution:   { desc: "Where callers found you, in their own words.", accent: "violet" },
  call_shape:    { desc: "When callers dropped off during the call.", accent: "rose" },
  questions:     { desc: "What guests asked about most.", accent: "teal" },
  trend:         { desc: "How conversation volume changed over the period.", accent: "sky" },
};

const metaFor = (panelKey) => PANEL_META[String(panelKey || "").replace(/^(voice|chat)_/, "")] || null;

// The shared empty state — an icon chip, the message and a quiet hint, centred both
// ways. `flex-1` lets it fill (and vertically centre within) a card that stretched
// to a taller sibling in its grid row; `min-h` gives it presence in a non-flex host
// (the drill set). Reused by every card so "no data" reads the same everywhere.
export const PanelEmpty = ({
  message = "No data for this period",
  hint = "Nothing to show for the selected dates — try a wider range.",
}) => (
  <div data-testid="panel-empty" className="flex flex-1 flex-col items-center justify-center text-center py-12 px-4 min-h-[180px]">
    <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 dark:bg-gray-800" aria-hidden="true">
      <svg className="h-6 w-6 text-gray-500 dark:text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3v18h18" />
        <path d="M7 14l3-3 3 2 4-5" strokeDasharray="2.5 2.5" />
      </svg>
    </span>
    <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{message}</p>
    {hint ? <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
  </div>
);

/**
 * The shared "couldn't load" state — shown when the request behind a card or drill
 * failed (a statement timeout, a permission error), so a failure never reads as
 * "No data for this period". Offers a retry when the host can re-run the request.
 * @param {{ message?: string, hint?: string, onRetry?: () => void }} props
 */
export const PanelLoadFailed = ({
  message = "Couldn't load this",
  hint = "The request failed or took too long. Try again, or pick a shorter date range.",
  onRetry,
}) => (
  <div data-testid="panel-load-failed" role="alert" className="flex flex-1 flex-col items-center justify-center text-center py-12 px-4 min-h-[180px]">
    <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 dark:bg-amber-900/30" aria-hidden="true">
      <svg className="h-6 w-6 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 9v4" />
        <path d="M12 17h.01" />
        <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
      </svg>
    </span>
    <p className="text-sm font-medium text-gray-700 dark:text-gray-200">{message}</p>
    {hint ? <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">{hint}</p> : null}
    {onRetry ? (
      <button
        type="button"
        onClick={onRetry}
        className="mt-3 inline-flex items-center min-h-[36px] px-3 rounded-md text-sm font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800"
      >
        Try again
      </button>
    ) : null}
  </div>
);

/**
 * Which panels on the page lost their data to a failed request, keyed by panelKey,
 * plus the page's retry. Provided once by the page's composition site so every
 * PanelShell can show PanelLoadFailed without each panel threading a prop.
 */
export const PanelLoadContext = React.createContext({ failedPanels: new Set(), onRetry: null });

// A "Refreshing…" overlay for a card that already has data and is re-fetching (e.g.
// a Phone↔Chat switch): the old figures dim under a translucent scrim + spinner
// instead of jumping to the new numbers with no signal. The initial-load case is the
// full skeleton; this is only for a re-fetch. Its host must be `relative`.
export const RefreshingOverlay = () => (
  <div
    data-testid="panel-refreshing"
    className="absolute inset-0 z-30 flex items-center justify-center rounded-xl bg-white/55 dark:bg-gray-900/55 backdrop-blur-[1px]"
    role="status"
    aria-live="polite"
  >
    <span className="inline-flex items-center gap-2 rounded-full bg-white/90 dark:bg-gray-800/90 px-3 py-1 text-xs font-medium text-gray-600 dark:text-gray-300 shadow-sm">
      <svg className="w-3.5 h-3.5 animate-spin text-[#32B1FF]" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="4" />
        <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="4" strokeLinecap="round" />
      </svg>
      Refreshing…
    </span>
  </div>
);

// PanelShell — the standard card chrome for every Voice/Chat Intelligence
// panel (plan Phase 2). Built on the repo's existing card look — NOT a new
// visual language. Slots:
//   - label:     the panel title (rendered at .gi-label, uppercase + muted).
//   - sentence:  the description under the title (say[panelKey]) at text-sm /
//                gray-500, closed with a bottom-border rule (progressive
//                enhancement — renders null when the sentence is absent, leaving NO
//                gap and NO placeholder node, per the plan's "data-no-sentence").
//   - evidence:  the chart/table/body (the real content).
//   - empty:     rendered INSTEAD of evidence when there is no data for the
//                period. Pass `hasData={false}` to show it; the parent decides
//                what "no data" means (the plan's "no-data-for-period" state).
//
// Wrapped in PanelErrorBoundary so one panel crashing/throwing degrades only
// that panel — the page keeps rendering (R-test: "PanelErrorBoundary degrading
// one panel without blanking the page").
//
// `fullWidth` drops the lg:col-span constraint so a panel can own its row
// (the funnel and trend chart both take full width per the spec).

const PanelShell = ({
  panelKey,
  label,
  say,
  // Fallback header description for a panel the backend say-map has no sentence for
  // (e.g. Time saved / Workload). The backend `say[panelKey]` sentence still wins
  // when present — this only fills the gap so the card is not left title-only.
  description,
  hasData = true,
  loading = false,
  fullWidth = false,
  className = "",
  emptyMessage = "No data for this period",
  // A decorative corner glyph. Taken as a PROP rather than passed in as a child,
  // because children only render when `hasData` — so a watermark supplied as a
  // child vanished exactly when the panel had least on it and most needed the
  // identity.
  watermark,
  watermarkTone = "brand",
  // True when the request behind this panel failed. Defaults to the page's
  // PanelLoadContext, so a panel need not pass it.
  failed,
  children,
}) => {
  const loadCtx = React.useContext(PanelLoadContext);
  const isFailed = failed ?? loadCtx.failedPanels.has(panelKey);
  const widthClass = fullWidth ? "lg:col-span-3" : "";
  const meta = metaFor(panelKey);
  // Backend sentence preferred, then the explicit prop, then the per-panel fallback
  // so the card is never title-only.
  const desc = say?.[panelKey] || description || meta?.desc;

  // While loading, the panel IS the skeleton. SkeletonCard draws its own card
  // chrome (rounded, shadow, border), so rendering it inside this component's
  // card produced a card inside a card. Keep the width class so a full-width
  // panel holds its column span and the grid does not reflow when data lands.
  if (loading && !hasData) {
    return (
      <PanelErrorBoundary label={label || panelKey}>
        <SkeletonCard lines={3} className={widthClass} />
      </PanelErrorBoundary>
    );
  }

  return (
    <PanelErrorBoundary label={label || panelKey}>
      {/* `relative` so a panel can bleed a decorative watermark off its own
          corner without escaping to the page. Purely a positioning context — it
          changes nothing about how any existing panel lays out. */}
      <section
        className={`relative flex flex-col bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 p-5 ${widthClass} ${className}`}
        aria-label={label}
      >
        {watermark && <CardWatermark icon={watermark} tone={watermarkTone} />}
        {/* Re-fetch feedback: a card that already has data and is loading again
            (Phone↔Chat switch, date change) shows the refreshing scrim rather than
            silently swapping numbers. Initial load is the skeleton returned above. */}
        {loading && hasData && <RefreshingOverlay />}
        {/* Card header — the title plus its description (the backend takeaway
            sentence, or the `description` fallback), closed off with a bottom-border
            rule that separates the header from the evidence, matching the house card
            header (user ruling 2026-09-04). The description renders nothing when both
            are absent; the header renders only when there is a title or a
            description, so a bare panel never gets an empty ruled header. */}
        {(label || desc) && (
          <div className="border-b border-gray-100 dark:border-gray-800 pb-3 mb-4">
            {label && (
              // Card title at the house card-title style (user ruling 2026-09-04,
              // matching the Rate Calendar card): text-base / semibold / near-black,
              // title case — NOT the uppercase muted gi-label it used to be.
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
                {label}
              </h3>
            )}
            {/* Description — the backend takeaway sentence, or the `description`
                fallback when the say-map has none, at text-xs / gray-500 (user
                ruling 2026-09-04). Renders nothing when both are absent, so a
                title-only card keeps a clean ruled header with no empty line. */}
            {desc && (
              <p className="text-xs text-gray-500 dark:text-gray-400">{desc}</p>
            )}
          </div>
        )}
        {/* Evidence slot — the real content, or the empty state. The `loading` race
            (a card falling to the empty state while its RPC is still in flight) is
            handled by the early skeleton return above. The empty state is a proper
            illustration-led block (user ruling 2026-09-04) — a soft icon chip, the
            message, and a quiet hint — not a lone centred line of grey text. */}
        {isFailed && !loading
          ? <PanelLoadFailed onRetry={loadCtx.onRetry || undefined} />
          : hasData ? children : <PanelEmpty message={emptyMessage} />}
      </section>
    </PanelErrorBoundary>
  );
};

export default PanelShell;
